import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { InputNumber } from 'primereact/inputnumber';
import { Chips } from 'primereact/chips';
import { Checkbox } from 'primereact/checkbox';
import axiosInstance from '../config/axios.config';

const EligibilityRuleTypes = () => {
    const [ruleTypes, setRuleTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [ruleTypeDialog, setRuleTypeDialog] = useState(false);
    const [deleteRuleTypeDialog, setDeleteRuleTypeDialog] = useState(false);
    const [selectedRuleType, setSelectedRuleType] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const toast = useRef(null);

    const [ruleType, setRuleType] = useState({
        code: '',
        name: '',
        description: '',
        data_type: 'string',
        suggested_fields: [],
        validation_rules: [],
        is_active: true,
        display_order: 0
    });

    const dataTypes = [
        { label: 'Text', value: 'string' },
        { label: 'Number', value: 'number' },
        { label: 'Yes/No', value: 'boolean' },
        { label: 'Date', value: 'date' }
    ];

    useEffect(() => {
        loadRuleTypes();
    }, []);

    const loadRuleTypes = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/eligibility-rule-types');
            if (response.data.success) {
                setRuleTypes(response.data.data);
            }
        } catch (error) {
            console.error('Error loading rule types:', error);
            toast.current.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: 'Failed to load rule types' 
            });
        } finally {
            setLoading(false);
        }
    };

    const openNew = () => {
        setRuleType({
            code: '',
            name: '',
            description: '',
            data_type: 'string',
            suggested_fields: [],
            validation_rules: [],
            is_active: true,
            display_order: 0
        });
        setSubmitted(false);
        setRuleTypeDialog(true);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setRuleTypeDialog(false);
    };

    const hideDeleteRuleTypeDialog = () => {
        setDeleteRuleTypeDialog(false);
    };

    const saveRuleType = async () => {
        setSubmitted(true);

        if (ruleType.code.trim() && ruleType.name.trim()) {
            try {
                let response;
                if (ruleType.id) {
                    response = await axiosInstance.put(`/eligibility-rule-types/${ruleType.id}`, ruleType);
                } else {
                    response = await axiosInstance.post('/eligibility-rule-types', ruleType);
                }

                if (response.data.success) {
                    toast.current.show({ 
                        severity: 'success', 
                        summary: 'Success', 
                        detail: `Rule type ${ruleType.id ? 'updated' : 'created'} successfully` 
                    });
                    loadRuleTypes();
                    setRuleTypeDialog(false);
                }
            } catch (error) {
                console.error('Error saving rule type:', error);
                const errorMessage = error.response?.data?.message || 'Failed to save rule type';
                toast.current.show({ 
                    severity: 'error', 
                    summary: 'Error', 
                    detail: errorMessage 
                });
            }
        }
    };

    const editRuleType = (ruleType) => {
        setRuleType({ ...ruleType });
        setRuleTypeDialog(true);
    };

    const confirmDeleteRuleType = (ruleType) => {
        setSelectedRuleType(ruleType);
        setDeleteRuleTypeDialog(true);
    };

    const deleteRuleType = async () => {
        try {
            const response = await axiosInstance.delete(`/eligibility-rule-types/${selectedRuleType.id}`);
            if (response.data.success) {
                toast.current.show({ 
                    severity: 'success', 
                    summary: 'Success', 
                    detail: 'Rule type deleted successfully' 
                });
                loadRuleTypes();
            }
        } catch (error) {
            console.error('Error deleting rule type:', error);
            const errorMessage = error.response?.data?.message || 'Failed to delete rule type';
            toast.current.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: errorMessage 
            });
        }
        setDeleteRuleTypeDialog(false);
        setSelectedRuleType(null);
    };

    const toggleStatus = async (ruleType) => {
        try {
            const response = await axiosInstance.post(`/eligibility-rule-types/${ruleType.id}/toggle-status`);
            if (response.data.success) {
                toast.current.show({ 
                    severity: 'success', 
                    summary: 'Success', 
                    detail: 'Status updated successfully' 
                });
                loadRuleTypes();
            }
        } catch (error) {
            console.error('Error toggling status:', error);
            toast.current.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: 'Failed to update status' 
            });
        }
    };

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _ruleType = { ...ruleType };
        _ruleType[`${name}`] = val;
        setRuleType(_ruleType);
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label="New Rule Type" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
            </React.Fragment>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label="Refresh" icon="pi pi-refresh" className="p-button-outlined" onClick={loadRuleTypes} />
            </React.Fragment>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                {!rowData.is_system && (
                    <>
                        <Button 
                            icon="pi pi-pencil" 
                            className="p-button-rounded p-button-success mr-2" 
                            onClick={() => editRuleType(rowData)} 
                            tooltip="Edit"
                        />
                        <Button 
                            icon="pi pi-trash" 
                            className="p-button-rounded p-button-danger mr-2" 
                            onClick={() => confirmDeleteRuleType(rowData)} 
                            tooltip="Delete"
                        />
                    </>
                )}
                <Button 
                    icon={rowData.is_active ? "pi pi-eye" : "pi pi-eye-slash"} 
                    className={`p-button-rounded ${rowData.is_active ? 'p-button-info' : 'p-button-warning'}`}
                    onClick={() => toggleStatus(rowData)} 
                    tooltip={rowData.is_active ? "Deactivate" : "Activate"}
                />
            </React.Fragment>
        );
    };

    const systemBodyTemplate = (rowData) => {
        return (
            <Tag 
                value={rowData.is_system ? 'System' : 'Custom'} 
                severity={rowData.is_system ? 'info' : 'success'}
            />
        );
    };

    const statusBodyTemplate = (rowData) => {
        return (
            <Tag 
                value={rowData.is_active ? 'Active' : 'Inactive'} 
                severity={rowData.is_active ? 'success' : 'warning'}
            />
        );
    };

    const dataTypeBodyTemplate = (rowData) => {
        const typeColors = {
            'string': 'primary',
            'number': 'info',
            'boolean': 'warning',
            'date': 'success'
        };
        return (
            <Tag 
                value={rowData.data_type_display || rowData.data_type} 
                severity={typeColors[rowData.data_type] || 'secondary'}
            />
        );
    };

    const suggestedFieldsBodyTemplate = (rowData) => {
        const fields = rowData.suggested_fields || [];
        if (fields.length === 0) return '-';
        
        return (
            <div className="flex flex-wrap gap-1">
                {fields.slice(0, 3).map((field, index) => (
                    <Tag key={index} value={field} severity="secondary" />
                ))}
                {fields.length > 3 && <Tag value={`+${fields.length - 3} more`} severity="info" />}
            </div>
        );
    };

    const ruleTypeDialogFooter = (
        <React.Fragment>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label="Save" icon="pi pi-check" className="p-button-text" onClick={saveRuleType} />
        </React.Fragment>
    );

    const deleteRuleTypeDialogFooter = (
        <React.Fragment>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteRuleTypeDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteRuleType} />
        </React.Fragment>
    );

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

                    <DataTable 
                        value={ruleTypes} 
                        paginator 
                        rows={10} 
                        rowsPerPageOptions={[5, 10, 25]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} rule types"
                        loading={loading}
                        responsiveLayout="scroll"
                        emptyMessage="No rule types found."
                    >
                        <Column field="code" header="Code" sortable style={{ minWidth: '10rem' }}></Column>
                        <Column field="name" header="Name" sortable style={{ minWidth: '12rem' }}></Column>
                        <Column field="description" header="Description" style={{ minWidth: '20rem' }}></Column>
                        <Column body={dataTypeBodyTemplate} header="Data Type" sortable style={{ minWidth: '8rem' }}></Column>
                        <Column body={suggestedFieldsBodyTemplate} header="Suggested Fields" style={{ minWidth: '15rem' }}></Column>
                        <Column field="display_order" header="Order" sortable style={{ minWidth: '6rem' }}></Column>
                        <Column body={systemBodyTemplate} header="Type" sortable style={{ minWidth: '6rem' }}></Column>
                        <Column body={statusBodyTemplate} header="Status" sortable style={{ minWidth: '6rem' }}></Column>
                        <Column body={actionBodyTemplate} exportable={false} style={{ minWidth: '10rem' }}></Column>
                    </DataTable>

                    <Dialog 
                        visible={ruleTypeDialog} 
                        style={{ width: '600px' }} 
                        header={ruleType.id ? 'Edit Rule Type' : 'New Rule Type'} 
                        modal 
                        className="p-fluid" 
                        footer={ruleTypeDialogFooter} 
                        onHide={hideDialog}
                    >
                        <div className="field">
                            <label htmlFor="code">Code *</label>
                            <InputText 
                                id="code" 
                                value={ruleType.code} 
                                onChange={(e) => onInputChange(e, 'code')} 
                                required 
                                autoFocus 
                                className={submitted && !ruleType.code ? 'p-invalid' : ''} 
                                disabled={ruleType.id ? true : false}
                            />
                            {submitted && !ruleType.code && <small className="p-error">Code is required.</small>}
                        </div>

                        <div className="field">
                            <label htmlFor="name">Name *</label>
                            <InputText 
                                id="name" 
                                value={ruleType.name} 
                                onChange={(e) => onInputChange(e, 'name')} 
                                required 
                                className={submitted && !ruleType.name ? 'p-invalid' : ''} 
                            />
                            {submitted && !ruleType.name && <small className="p-error">Name is required.</small>}
                        </div>

                        <div className="field">
                            <label htmlFor="description">Description</label>
                            <InputTextarea 
                                id="description" 
                                value={ruleType.description} 
                                onChange={(e) => onInputChange(e, 'description')} 
                                rows={3} 
                                cols={20} 
                            />
                        </div>

                        <div className="field">
                            <label htmlFor="data_type">Data Type *</label>
                            <Dropdown 
                                id="data_type" 
                                value={ruleType.data_type} 
                                options={dataTypes} 
                                onChange={(e) => onInputChange(e, 'data_type')} 
                                placeholder="Select a Data Type"
                            />
                        </div>

                        <div className="field">
                            <label htmlFor="suggested_fields">Suggested Fields</label>
                            <Chips 
                                id="suggested_fields" 
                                value={ruleType.suggested_fields} 
                                onChange={(e) => setRuleType({ ...ruleType, suggested_fields: e.value })}
                                placeholder="Add field names and press Enter"
                            />
                            <small>Enter field names that this rule type typically validates</small>
                        </div>

                        <div className="field">
                            <label htmlFor="display_order">Display Order</label>
                            <InputNumber 
                                id="display_order" 
                                value={ruleType.display_order} 
                                onValueChange={(e) => setRuleType({ ...ruleType, display_order: e.value })}
                                min={0}
                            />
                        </div>

                        <div className="field-checkbox">
                            <Checkbox 
                                inputId="is_active" 
                                checked={ruleType.is_active} 
                                onChange={e => setRuleType({ ...ruleType, is_active: e.checked })} 
                            />
                            <label htmlFor="is_active">Active</label>
                        </div>
                    </Dialog>

                    <Dialog 
                        visible={deleteRuleTypeDialog} 
                        style={{ width: '450px' }} 
                        header="Confirm" 
                        modal 
                        footer={deleteRuleTypeDialogFooter} 
                        onHide={hideDeleteRuleTypeDialog}
                    >
                        <div className="confirmation-content">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            {selectedRuleType && (
                                <span>
                                    Are you sure you want to delete <b>{selectedRuleType.name}</b>?
                                </span>
                            )}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default EligibilityRuleTypes;